import { IFormValues } from '@estee/elc-form/typings/views/Form';
import { RouterStore } from '@estee/elc-router';

import { BitType, IUserSignInApiPayload } from '~api/RampPgApiSdk';
import { IamType, OTP_FLOW, OTP_TYPE_EMAIL, RiskLevels, StorageKey } from '~constants/Constants';
import { SignInType } from '~constants/SignInTypeEnum';
import { IAccountSignInFormValues } from '~domain/entities/AccountSignInFormStore';
import { IGuestSignInFormValues } from '~domain/entities/GuestSignInFormStore';
import { RampFormStore } from '~domain/entities/RampFormStore';
import { ConfigRepository } from '~repositories/ConfigRepository';
import { MessageGroup, MessageRepository } from '~repositories/MessageRepository';
import { UserRepository } from '~repositories/UserRepository';
import {
    ACCOUNT_HOME_IKALAR_ANCHOR,
    ACCOUNT_PREFERENCE_URL,
    CART_URL,
    SOCIAL_SIGN_UP_SUCCESS_ROUTE
} from '~routes/routes';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/RampService';
import {
    emitAnalyticsEvent,
    eventTag,
    TaggingEventProviderType,
    TaggingEventSourceType
} from '~utils/Analytics';
import { setLocalStorageItem } from '~utils/LocalStorageUtils';
import { checkIsMobileDevice } from '~utils/mobile';
import {
    getSocialSignUpProviderSource,
    getSocialSignUpProviderSourceFromState
} from '~utils/SocialUtils';
import { AccountAccessPageViewController } from '~views/account/AccountAccessPageViewController';

import { UpdateUserIfNeededAfterSignInUseCase } from './UpdateUserIfNeededAfterSignInUseCase';

type SignInDataType = IAccountSignInFormValues | IGuestSignInFormValues;
type UnionToIntersection<T> = (T extends unknown ? (x: T) => unknown : never) extends (
    x: infer R
) => unknown
    ? R
    : never;

interface IGetLocationAfterSignIn {
    isSocialProfile: boolean;
    isConsentFlow: boolean;
    onSuccessRedirectUrl: string;
    forwardToCart?: BitType;
}

interface ILoginSubmittedUseCase {
    signInData: SignInDataType;
    /** FormStore to update while form is submitting or there is sign-in error */
    formStore?: RampFormStore<IFormValues>;
    signInType?: SignInType;
    /** Emit analytics events, defaults to true if not set (if originated by successful sign-up, no need to emit events) */
    emitTrackingEvent?: boolean;
    isSignInAfterSignUp?: boolean;
}

export class LoginSubmittedUseCase {
    private userRepository: UserRepository;
    private configRepository: ConfigRepository;
    private signInData: SignInDataType;
    private formStore?: RampFormStore<IFormValues>;
    private signInType?: SignInType;
    private emitTrackingEvent: boolean;
    private routerStore: RouterStore;
    private messageRepository: MessageRepository;
    private accessViewController: AccountAccessPageViewController;
    private isSignInAfterSignUp: boolean;

    constructor({
        signInData,
        signInType,
        formStore,
        emitTrackingEvent = true,
        isSignInAfterSignUp = false
    }: ILoginSubmittedUseCase) {
        this.signInData = signInData;
        this.formStore = formStore;
        this.signInType = signInType;
        this.emitTrackingEvent = emitTrackingEvent;
        this.userRepository = diContainer.get(serviceNames.userRepository);
        this.routerStore = diContainer.get(serviceNames.routerStore);
        this.configRepository = diContainer.get(serviceNames.configRepository);
        this.messageRepository = diContainer.get(serviceNames.messageRepository);
        this.accessViewController = diContainer.get(serviceNames.accountAccessViewController);
        this.isSignInAfterSignUp = isSignInAfterSignUp;
    }

    private get eventData() {
        let provider: TaggingEventProviderType;
        if (this.signInType === undefined || this.signInType === SignInType.GUEST) {
            provider = 'standard';
        } else {
            provider = this.signInType;
        }

        const { signInEventSource } = this.userRepository;

        return {
            provider: `${provider}`,
            source:
                this.signInType === SignInType.GUEST
                    ? TaggingEventSourceType.GUEST_CHECKOUT
                    : signInEventSource
        };
    }

    private getLocationAfterSignIn = ({
        isSocialProfile,
        isConsentFlow,
        onSuccessRedirectUrl,
        forwardToCart
    }: IGetLocationAfterSignIn): string => {
        const { posIKalarCallbackUrl } = this.userRepository;
        const {
            data: { isCheckoutFlow }
        } = this.accessViewController;
        const hasProductsInCart = !!forwardToCart && isCheckoutFlow;

        if (isSocialProfile && !isConsentFlow) {
            // After creating complete profile with social signup journey, take user to account preference page
            return ACCOUNT_PREFERENCE_URL;
        } else if (hasProductsInCart) {
            return CART_URL;
        } else if (posIKalarCallbackUrl && checkIsMobileDevice()) {
            return ACCOUNT_HOME_IKALAR_ANCHOR;
        } else {
            return onSuccessRedirectUrl;
        }
    };

    public execute = async () => {
        const {
            signIn,
            consentForm: { isConsentFlow, callbackState }
        } = this.userRepository;
        const { clearMessagesByGroup } = this.messageRepository;
        const { setSubmitting } = this.formStore ?? {};

        const {
            isSocialProfile,
            onSuccessRedirectUrl,
            journey: { postSubmitRoute }
        } = this.configRepository;

        setSubmitting?.(true);

        if (this.signInType === SignInType.GUEST) {
            clearMessagesByGroup(MessageGroup.GUEST_CHECKOUT);
        } else {
            clearMessagesByGroup(MessageGroup.SIGN_IN);
        }

        const data = this.buildUserSignInApiPayload();

        if (data) {
            const {
                error: errorKey,
                errorCode,
                riskLevel,
                userId,
                hmac,
                forwardToCart
            } = await signIn(data, this.signInType);

            if (
                this.configRepository.rampCommon?.fraudVerificationEnabled &&
                riskLevel === RiskLevels.MEDIUM
            ) {
                this.userRepository.otpStore.setData({
                    hmac,
                    type: OTP_TYPE_EMAIL,
                    showOtp: true,
                    flow: OTP_FLOW.FRAUD
                });
                setSubmitting?.(false);
            } else if (!errorKey) {
                // TODO: Use a dynamic value when Keep Me Signed In feature is enabled
                const eventData = { ...this.eventData, userId, rememberMe: false };

                this.emitTrackingEvent &&
                    emitAnalyticsEvent(eventTag.SIGN_IN_SUCCESSFUL, eventData);

                await new UpdateUserIfNeededAfterSignInUseCase(
                    userId,
                    this.isSignInAfterSignUp
                ).execute();

                // Sign in from social sign up flow
                // 1. Existing user sign in
                // 2. Social login redirect to sign in
                const isSignInWithSocialSignUpFlow =
                    postSubmitRoute === SOCIAL_SIGN_UP_SUCCESS_ROUTE ||
                    (callbackState && !!getSocialSignUpProviderSourceFromState(callbackState));
                if (isSignInWithSocialSignUpFlow) {
                    this.routerStore.changeCurrentRoute(SOCIAL_SIGN_UP_SUCCESS_ROUTE);
                } else {
                    const locationAfterSignIn = this.getLocationAfterSignIn({
                        isSocialProfile,
                        isConsentFlow,
                        onSuccessRedirectUrl,
                        forwardToCart
                    });

                    if (this.signInType !== SignInType.GUEST) {
                        setLocalStorageItem(StorageKey.CROSS_PLATFORM_AUTH, 'true');
                    }

                    this.routerStore.changeLocation(locationAfterSignIn);
                }
            } else {
                const messageGroup =
                    this.signInType === SignInType.GUEST
                        ? MessageGroup.GUEST_CHECKOUT
                        : MessageGroup.SIGN_IN;
                this.messageRepository.setMessages(messageGroup, [{ transKey: errorKey }]);

                this.emitTrackingEvent &&
                    emitAnalyticsEvent(eventTag.SIGN_IN_FAILED, {
                        ...this.eventData,
                        type: errorCode as string
                    });
                setSubmitting?.(false);
                if (this.formStore?.values?.password) {
                    this.formStore.setFieldValue('password', '');
                }
            }
        }
    };

    private buildUserSignInApiPayload = () => {
        const { signInType, signInData } = this;

        const {
            newUserEmailAddress,
            pcEmailPromotions = 0,
            accountIdentifier,
            password = ''
        } = signInData as UnionToIntersection<SignInDataType>;

        const signInTypeMappings = {
            [SignInType.GUEST]: {
                newUserEmailAddress,
                ...(pcEmailPromotions > 0 && { pcEmailPromotions: 1 })
            },
            default: { emailAddress: accountIdentifier || '', password }
        } as Record<string, IUserSignInApiPayload>;

        const data =
            signInTypeMappings[signInType as keyof typeof signInTypeMappings] ||
            signInTypeMappings.default;
        const { iamType } = this.configRepository;
        if (iamType === IamType.SOCIAL_SIGN_UP) {
            data.providerSource = getSocialSignUpProviderSource() || '';
        }

        return data;
    };
}
